import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import Form from "components/shared/Form";
import AutomaticBreaks from "./AutomaticBreaks";
import ManualBreaks from "./ManualBreaks";
import RestBreaks from "./RestBreaks";
import { useProfile } from "../../../../../../hooks/useProfile";
import { useChangeUserInformationMutation } from "../../../../../../api/useChangeUserInformationMutation";
import type { TFunction } from "i18next";
import "./styles.scss";

const breakRowSchema = z.object({
    amount: z.union([z.string(), z.number()]).optional(),
    after: z.union([z.string(), z.number()]).optional(),
});

type BreakRow = z.infer<typeof breakRowSchema>;

const validateBreakRows = (t: TFunction, rows: BreakRow[] | undefined, path: (string | number)[], ctx: z.RefinementCtx) => {
    (rows ?? []).forEach((row, index) => {
        if (!row.amount) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "amount"], message: t("required-amount") });
        } else if (Number(row.amount) <= 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "amount"], message: t("amount-bigger-than-zero") });
        }

        if (!row.after) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "after"], message: t("required-after") });
        } else if (Number(row.after) <= 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "after"], message: t("after-bigger-than-zero") });
        } else if (row.amount && Number(row.after) < Number(row.amount)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "after"], message: t("after-bigger-than-amount") });
        }
    });
};

const createBreaksSchema = (t: TFunction) =>
    z.object({
        breaks: z.object({
            automatic: z.object({
                status: z.boolean().optional(),
                payCode: z.any().nullable().optional(),
                hideBreakAndDeductTotal: z.boolean().optional(),
                allowOverride: z.boolean().optional(),
                breaks: z.array(breakRowSchema).optional(),
            }),
            manual: z.object({
                status: z.boolean().optional(),
                payCode: z.any().nullable().optional(),
                mandatory: z.boolean().optional(),
                duration: z.union([z.string(), z.number()]).optional(),
            }),
            rest: z.object({
                status: z.boolean().optional(),
                breaks: z.array(breakRowSchema).optional(),
            }),
        }),
    }).superRefine((values, ctx) => {
        const { automatic, manual, rest } = values.breaks;

        if (automatic.status) {
            if (!automatic.payCode?.id) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", "automatic", "payCode"], message: t("required-type") });
            }
            validateBreakRows(t, automatic.breaks, ["breaks", "automatic", "breaks"], ctx);
        }

        if (manual.status) {
            if (!manual.payCode?.id) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", "manual", "payCode"], message: t("required-type") });
            }
            if (manual.mandatory) {
                if (!manual.duration) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", "manual", "duration"], message: t("required-duration") });
                } else if (Number(manual.duration) <= 0) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", "manual", "duration"], message: t("duration-bigger-than-zero") });
                }
            }
        }

        if (rest.status) {
            validateBreakRows(t, rest.breaks, ["breaks", "rest", "breaks"], ctx);
        }
    });

function Breaks({ close }: { close: () => void }) {
    const { t } = useTranslation();
    const { user } = useProfile();
    const { changeCalculationGroupRules } = useChangeUserInformationMutation();

    const defaultValues = useMemo(() => ({
        breaks: {
            automatic: {
                status: false,
                payCode: null,
                hideBreakAndDeductTotal: false,
                allowOverride: false,
                breaks: [{ amount: undefined, after: undefined }],
                ...(user?.calculationGroupRules?.breaks?.automatic || {}),
            },
            manual: {
                status: false,
                payCode: null,
                mandatory: false,
                duration: undefined,
                ...(user?.calculationGroupRules?.breaks?.manual || {}),
            },
            rest: {
                status: false,
                breaks: [{ amount: undefined, after: undefined }],
                ...(user?.calculationGroupRules?.breaks?.rest || {}),
            },
        },
    }), [user]);

    const schema = useMemo(() => createBreaksSchema(t), [t]);

    const form = useForm({ defaultValues, resolver: zodResolver(schema) });

    const onFinish = (value: typeof defaultValues) => {
        changeCalculationGroupRules.mutate({ breaks: value.breaks }, {
            onSuccess: () => {
                toast.success(t("changes-saved-successfully"));
                close();
            },
        });
    };

    return (
        <Form form={form} onFinish={onFinish} className="tw-flex tw-flex-col tw-h-full">
            <div className="breaks-form tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pb-4">
                <Card>
                    <CardHeader>
                        <CardTitle>{t("breaks")}</CardTitle>
                    </CardHeader>
                    <CardContent className="tw-space-y-6">
                        <AutomaticBreaks disabled={false} />
                        <ManualBreaks disabled={false} />
                        <RestBreaks disabled={false} />
                    </CardContent>
                </Card>
            </div>

            <div className="tw-shrink-0 tw-flex tw-justify-end tw-gap-2 tw-pt-4 tw-border-t">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={close}
                    disabled={changeCalculationGroupRules.isPending}
                >
                    {t("cancel")}
                </Button>
                <Button
                    type="submit"
                    size="sm"
                    disabled={changeCalculationGroupRules.isPending}
                >
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default Breaks;
